import React from "react";
import { X, Plus, ChevronUp, ChevronDown } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const MAX_REPLIES = 6;
const MAX_CHARS = 100;

const QuickReplyListEditor = ({ values, onChange }) => {
  const { translate } = useLanguage();
  const items = values || [];

  const updateAt = (idx, v) => {
    const next = [...items];
    next[idx] = v;
    onChange(next);
  };

  const removeAt = (idx) => {
    const next = items.filter((_, i) => i !== idx);
    onChange(next.length === 0 ? null : next);
  };

  const moveBy = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const add = () => {
    if (items.length >= MAX_REPLIES) return;
    onChange([...items, ""]);
  };

  return (
    <div className="space-y-2">
      {items.map((value, idx) => {
        const len = value.length;
        const over = len > MAX_CHARS;
        const canUp = idx > 0;
        const canDown = idx < items.length - 1;
        return (
          <div key={idx} className="flex items-center gap-2">
            <div className="flex flex-col" style={{ marginRight: -4 }}>
              <button
                type="button"
                onClick={() => moveBy(idx, -1)}
                disabled={!canUp}
                aria-label={translate("moveUp") || "Move up"}
                className="p-0.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ color: "var(--color-text-muted)" }}
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveBy(idx, 1)}
                disabled={!canDown}
                aria-label={translate("moveDown") || "Move down"}
                className="p-0.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ color: "var(--color-text-muted)" }}
              >
                <ChevronDown size={14} />
              </button>
            </div>
            <div className="flex-1 relative">
              <input
                type="text"
                value={value}
                onChange={(e) => updateAt(idx, e.target.value)}
                // Soft cap: hard-stop at +20 above MAX_CHARS so the in-input
                // counter (and the warning border) can still surface "over"
                // values briefly instead of silently truncating the user's
                // keystrokes at the soft limit.
                maxLength={MAX_CHARS + 20}
                className="field-input w-full pr-14"
                placeholder={translate("quickReplyPlaceholder") || "Quick reply text"}
                style={{ borderColor: over ? "var(--color-warning, #f59e0b)" : undefined }}
              />
              <span
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium tabular-nums pointer-events-none"
                style={{ color: over ? "var(--color-warning, #f59e0b)" : "var(--color-text-muted)" }}
              >
                {len}/{MAX_CHARS}
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeAt(idx)}
              aria-label={`Remove quick reply: ${value}`}
              className="p-1.5 rounded hover:bg-gray-100 transition-colors"
              style={{ color: "var(--color-text-muted)" }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
      <div className="flex items-center justify-between">
        {items.length < MAX_REPLIES ? (
          <button
            type="button"
            onClick={add}
            className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors hover:bg-gray-100"
            style={{ color: "var(--color-mint)" }}
          >
            <Plus size={14} />
            {translate("addQuickReply") || "Add quick reply"}
          </button>
        ) : (
          <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
            {translate("tooManyQuickReplies") || `Maximum ${MAX_REPLIES} quick replies.`}
          </p>
        )}
        <p className="text-xs tabular-nums" style={{ color: "var(--color-text-muted)" }}>
          {items.length}/{MAX_REPLIES}
        </p>
      </div>
    </div>
  );
};

export default QuickReplyListEditor;
